import React, { useCallback } from 'react';
import { Box } from '@chakra-ui/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TaskColumn from './TaskColumn';
import { Task, Column } from '../types';
import {
  KANBAN_COLUMN_MAX_WIDTH,
  KANBAN_COLUMN_MIN_WIDTH,
} from '../hooks/useKanbanColumnWidths';

interface SortableColumnProps {
  column: Column;
  tasks: Task[];
  width: number;
  onWidthChange: (width: number) => void;
  onWidthReset: () => void;
  onTaskClick: (task: Task) => void;
  onDelete?: (columnId: string) => void;
  isFinished?: boolean;
  onSetAsFinished?: (columnId: string) => void;
  canReorderColumns?: boolean;
}

const GripIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" opacity={0.6}>
    <circle cx="6" cy="8" r="2" />
    <circle cx="12" cy="8" r="2" />
    <circle cx="18" cy="8" r="2" />
    <circle cx="6" cy="14" r="2" />
    <circle cx="12" cy="14" r="2" />
    <circle cx="18" cy="14" r="2" />
  </svg>
);

const SortableColumn: React.FC<SortableColumnProps> = ({
  column,
  tasks,
  width,
  onWidthChange,
  onWidthReset,
  onTaskClick,
  onDelete,
  isFinished,
  onSetAsFinished,
  canReorderColumns = true,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id, disabled: !canReorderColumns });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const startResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = width;

      const onMove = (ev: MouseEvent) => {
        const next = Math.min(
          KANBAN_COLUMN_MAX_WIDTH,
          Math.max(KANBAN_COLUMN_MIN_WIDTH, startWidth + ev.clientX - startX),
        );
        onWidthChange(next);
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [onWidthChange, width],
  );

  return (
    <Box
      ref={setNodeRef}
      style={style}
      position="relative"
      flexShrink={0}
      w={{ base: '100%', md: `${width}px` }}
      minW={{ base: '100%', md: `${KANBAN_COLUMN_MIN_WIDTH}px` }}
      maxW={{ base: '100%', md: `${KANBAN_COLUMN_MAX_WIDTH}px` }}
      display="flex"
      flexDirection="column"
    >
      <TaskColumn
        id={column.id}
        title={column.title}
        color={column.color}
        tasks={tasks}
        onTaskClick={onTaskClick}
        onDelete={onDelete}
        isFinished={isFinished}
        onSetAsFinished={onSetAsFinished}
        dragHandle={canReorderColumns ? (
          <Box
            {...attributes}
            {...listeners}
            cursor="grab"
            _active={{ cursor: 'grabbing' }}
            p={1}
            mr={1}
            borderRadius="md"
            _hover={{ bg: 'rgba(0,0,0,0.06)' }}
            title="Drag to reorder stage"
          >
            <GripIcon />
          </Box>
        ) : undefined}
      />
      <Box
        display={{ base: 'none', md: 'block' }}
        position="absolute"
        top={0}
        right={0}
        h="100%"
        w="8px"
        cursor="col-resize"
        zIndex={2}
        onMouseDown={startResize}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onWidthReset();
        }}
        title="Drag to resize stage · double-click to reset width"
        _hover={{ bg: 'blue.200' }}
        borderRadius="md"
      />
    </Box>
  );
};

export default SortableColumn;
